import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionGraph = ({ selectedAddress, onNodeClick, filters }) => {
  const svgRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Mock transaction graph data
  const graphData = {
    nodes: [
      {
        id: 'addr1',
        address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
        label: 'Genesis Wallet',
        type: 'origin',
        riskScore: 0,
        balance: 50.0,
        x: 400,
        y: 200,
        isExpanded: true
      },
      {
        id: 'addr2',
        address: '1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2',
        label: 'Suspicious Wallet',
        type: 'suspicious',
        riskScore: 85,
        balance: 25.5,
        x: 600,
        y: 150,
        isExpanded: false
      },
      {
        id: 'addr3',
        address: '3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy',
        label: 'Exchange Deposit',
        type: 'exchange',
        riskScore: 20,
        balance: 15.2,
        x: 800,
        y: 200,
        isExpanded: false
      },
      {
        id: 'addr4',
        address: '1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX',
        label: 'Mixing Service',
        type: 'mixer',
        riskScore: 95,
        balance: 8.7,
        x: 600,
        y: 300,
        isExpanded: false
      },
      {
        id: 'addr5',
        address: '1Lbcfr7sAHTD9CgdQo3HTMTkV8LK4ZnX71',
        label: 'Split Wallet A',
        type: 'split',
        riskScore: 60,
        balance: 4.3,
        x: 750,
        y: 350,
        isExpanded: false
      },
      {
        id: 'addr6',
        address: '1MK2SgwjheyYKuYdgVjjyJFTvJ5wpGmxaj',
        label: 'Split Wallet B',
        type: 'split',
        riskScore: 60,
        balance: 4.4,
        x: 450,
        y: 350,
        isExpanded: false
      }
    ],
    edges: [
      {
        id: 'edge1',
        source: 'addr1',
        target: 'addr2',
        amount: 25.5,
        timestamp: '2024-09-14T10:30:00Z',
        txHash: '0x1a2b3c4d5e6f...',
        confirmations: 156,
        fee: 0.0001
      },
      {
        id: 'edge2',
        source: 'addr2',
        target: 'addr3',
        amount: 15.2,
        timestamp: '2024-09-14T11:45:00Z',
        txHash: '0x2b3c4d5e6f7a...',
        confirmations: 142,
        fee: 0.0002
      },
      {
        id: 'edge3',
        source: 'addr2',
        target: 'addr4',
        amount: 8.7,
        timestamp: '2024-09-14T12:15:00Z',
        txHash: '0x3c4d5e6f7a8b...',
        confirmations: 138,
        fee: 0.0003
      },
      {
        id: 'edge4',
        source: 'addr4',
        target: 'addr5',
        amount: 4.3,
        timestamp: '2024-09-14T13:20:00Z',
        txHash: '0x4d5e6f7a8b9c...',
        confirmations: 125,
        fee: 0.0002
      },
      {
        id: 'edge5',
        source: 'addr4',
        target: 'addr6',
        amount: 4.4,
        timestamp: '2024-09-14T13:25:00Z',
        txHash: '0x5e6f7a8b9c0d...',
        confirmations: 124,
        fee: 0.0002
      }
    ]
  };

  const getNodeColor = (type, riskScore) => {
    switch (type) {
      case 'origin':
        return '#059669'; // emerald-600
      case 'suspicious':
        return '#DC2626'; // red-600
      case 'exchange':
        return '#1E3A8A'; // blue-800
      case 'mixer':
        return '#7C2D12'; // red-900
      case 'split':
        return '#D97706'; // amber-600
      default:
        return '#64748B'; // slate-500
    }
  };

  const getNodeSize = (balance) => {
    const minSize = 20;
    const maxSize = 40;
    const normalizedBalance = Math.min(balance / 50, 1);
    return minSize + (maxSize - minSize) * normalizedBalance;
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.3));
  };

  const handleResetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    onNodeClick && onNodeClick(node);
  };

  const handleMouseDown = (e) => {
    if (e?.target?.tagName === 'svg') {
      setIsDragging(true);
      setDragStart({ x: e?.clientX - panOffset?.x, y: e?.clientY - panOffset?.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanOffset({
        x: e?.clientX - dragStart?.x,
        y: e?.clientY - dragStart?.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  return (
    <div className="bg-surface border border-border rounded-lg shadow-forensic-card h-full flex flex-col">
      {/* Graph Controls */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="GitBranch" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Transaction Flow</h3>
          {selectedAddress && (
            <span className="text-sm text-text-secondary bg-muted px-2 py-1 rounded">
              {selectedAddress?.slice(0, 8)}...{selectedAddress?.slice(-6)}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 0.3}
          >
            <Icon name="ZoomOut" size={16} />
          </Button>
          <span className="text-sm text-text-secondary min-w-12 text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 3}
          >
            <Icon name="ZoomIn" size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetView}
          >
            <Icon name="RotateCcw" size={16} />
          </Button>
        </div>
      </div>
      {/* Graph Visualization */}
      <div className="flex-1 relative overflow-hidden bg-slate-50">
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          className="cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          style={{
            transform: `translate(${panOffset?.x}px, ${panOffset?.y}px) scale(${zoomLevel})`
          }}
        >
          {/* Grid Pattern */}
          <defs>
            <pattern
              id="grid"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 20 0 L 0 0 0 20"
                fill="none"
                stroke="#E2E8F0"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Edges */}
          {graphData?.edges?.map((edge) => {
            const sourceNode = graphData?.nodes?.find(n => n?.id === edge?.source);
            const targetNode = graphData?.nodes?.find(n => n?.id === edge?.target);
            
            if (!sourceNode || !targetNode) return null;

            return (
              <g key={edge?.id}>
                <line
                  x1={sourceNode?.x}
                  y1={sourceNode?.y}
                  x2={targetNode?.x}
                  y2={targetNode?.y}
                  stroke="#64748B"
                  strokeWidth="2"
                  markerEnd="url(#arrowhead)"
                />
                {/* Transaction Amount Label */}
                <text
                  x={(sourceNode?.x + targetNode?.x) / 2}
                  y={(sourceNode?.y + targetNode?.y) / 2 - 10}
                  textAnchor="middle"
                  className="text-xs fill-text-secondary font-medium"
                >
                  {edge?.amount} BTC
                </text>
              </g>
            );
          })}

          {/* Arrow Marker */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#64748B"
              />
            </marker>
          </defs>

          {/* Nodes */}
          {graphData?.nodes?.map((node) => {
            const nodeSize = getNodeSize(node?.balance);
            const nodeColor = getNodeColor(node?.type, node?.riskScore);
            const isSelected = selectedNode?.id === node?.id;

            return (
              <g key={node?.id}>
                {/* Node Circle */}
                <circle
                  cx={node?.x}
                  cy={node?.y}
                  r={nodeSize}
                  fill={nodeColor}
                  stroke={isSelected ? "#F59E0B" : "#FFFFFF"}
                  strokeWidth={isSelected ? "3" : "2"}
                  className="cursor-pointer hover:opacity-80 transition-forensic"
                  onClick={() => handleNodeClick(node)}
                />
                {/* Risk Score Badge */}
                {node?.riskScore > 50 && (
                  <circle
                    cx={node?.x + nodeSize - 8}
                    cy={node?.y - nodeSize + 8}
                    r="8"
                    fill="#DC2626"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                  />
                )}
                {/* Node Label */}
                <text
                  x={node?.x}
                  y={node?.y + nodeSize + 15}
                  textAnchor="middle"
                  className="text-xs fill-text-primary font-medium pointer-events-none"
                >
                  {node?.label}
                </text>
                {/* Balance */}
                <text
                  x={node?.x}
                  y={node?.y + nodeSize + 28}
                  textAnchor="middle"
                  className="text-xs fill-text-secondary pointer-events-none"
                >
                  {node?.balance} BTC
                </text>
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <div className="absolute top-4 right-4 bg-surface border border-border rounded-lg p-3 shadow-forensic-card">
          <h4 className="text-sm font-semibold text-text-primary mb-2">Legend</h4>
          <div className="space-y-2">
            {[
              { type: 'origin', label: 'Origin', color: '#059669' },
              { type: 'suspicious', label: 'Suspicious', color: '#DC2626' },
              { type: 'exchange', label: 'Exchange', color: '#1E3A8A' },
              { type: 'mixer', label: 'Mixer', color: '#7C2D12' },
              { type: 'split', label: 'Split', color: '#D97706' }
            ]?.map((item) => (
              <div key={item?.type} className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item?.color }}
                />
                <span className="text-xs text-text-secondary">{item?.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Graph Statistics */}
      <div className="p-4 border-t border-border bg-muted">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-text-primary">
              {graphData?.nodes?.length}
            </div>
            <div className="text-xs text-text-secondary">Addresses</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-text-primary">
              {graphData?.edges?.length}
            </div>
            <div className="text-xs text-text-secondary">Transactions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-error">
              {graphData?.nodes?.filter(n => n?.riskScore > 70)?.length}
            </div>
            <div className="text-xs text-text-secondary">High Risk</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-text-primary">
              {graphData?.edges?.reduce((sum, edge) => sum + edge?.amount, 0)?.toFixed(2)}
            </div>
            <div className="text-xs text-text-secondary">Total BTC</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionGraph;